import React, {useEffect, useState} from 'react';
import {loginResponseSelector, loginSelector} from '../../../redux/selectors/login';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../../redux';
import {reduxForm, initialize, SubmissionError, SubmitHandler, FormErrors, change} from 'redux-form';
import View from './view';
import {LoginState} from '../../../shared/models/login/loginStep';
import {Forms} from '../../../shared/models/forms';
import {EnterForm} from '../../../shared/models/login';
import {validationPipe, FormValidationError, ValidationItem} from '../../../shared/pipes';
import {checkLogin, getEsiaLink} from '../../../redux/actions/login';
import {setRuntimeLoginStepState} from '../../../redux/actions/runtime/loginStep';
import {LoginType} from '../../../shared/models/api';
import {onlyDigitsPipe} from '../../../shared/pipes';
import {RegExps} from '../../../shared/constants/regexps';
import {ApiEnvironments, Settings} from '../../../shared/constants/config';
import {startDigits} from '../../../shared/constants';

type ValidationError = Error & {
  data: {message: string}[];
};

const getFormErrors = (formData: EnterForm, needCard: boolean) => {
  const baseValidationArray: ValidationItem[] = [
    {
      names: 'identificator',
      error: 'Обязательное поле',
      type: 'required',
    },
  ];
  const patternString = [/^\d{11}$/, RegExps.checkEsksoCardNumber, RegExps.checkPhoneNumber]
    .map((pattern) => pattern.toString().slice(1, -1))
    .join('|');

  const identificatorPattern = new RegExp(patternString);
  const rawInputValue = needCard ? formData?.cardNumber ?? '' : formData?.identificator ?? '';
  const inputValue = onlyDigitsPipe(rawInputValue);

  const isSnils = RegExps.checkEsksoSnilsNumber.test(inputValue);
  const isPhone = RegExps.checkPhoneNumber.test(inputValue);
  const startCardDigits = inputValue.startsWith(startDigits);
  const isCommonInputLength = inputValue.length < 11;
  const isCardDigitsLength = inputValue.length < 16;
  const isMaxCardDigitsLength = inputValue.length === 16;

  if (isCommonInputLength) {
    baseValidationArray.push({
      names: 'identificator',
      error: `Укажите номер карты (номер должен начинаться с ${startDigits}), телефон или СНИЛС`,
      type: 'pattern',
      pattern: identificatorPattern,
    });
  } else {
    if (isCardDigitsLength && startCardDigits && !(isSnils || isPhone)) {
      baseValidationArray.push({
        names: 'identificator',
        error: 'Длина номера должна равняться 16 символам',
        type: 'pattern',
        pattern: identificatorPattern,
      });
    }
    if ((isCardDigitsLength || isMaxCardDigitsLength) && !startCardDigits && !(isSnils || isPhone)) {
      baseValidationArray.push({
        names: 'identificator',
        error: `Номер карты должен начинаться с ${startDigits}`,
        type: 'pattern',
        pattern: identificatorPattern,
      });
    }
  }

  if (needCard) {
    baseValidationArray.push({
      names: 'cardNumber',
      error: 'Обязательное поле',
      type: 'required',
    });
    if (isCommonInputLength) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: `Укажите номер карты (номер должен начинаться с ${startDigits})`,
        type: 'pattern',
        pattern: new RegExp(RegExps.checkEsksoCardNumber),
      });
    } else {
      if (isCardDigitsLength && startCardDigits) {
        baseValidationArray.push({
          names: 'cardNumber',
          error: 'Длина номера должна равняться 16 символам',
          type: 'pattern',
          pattern: new RegExp(RegExps.checkEsksoCardNumber),
        });
      } else if ((isCardDigitsLength || isMaxCardDigitsLength) && !startCardDigits) {
        baseValidationArray.push({
          names: 'cardNumber',
          error: `Номер карты должен начинаться с ${startDigits}`,
          type: 'pattern',
          pattern: new RegExp(RegExps.checkEsksoCardNumber),
        });
      }
    }
  }

  return validationPipe(formData, baseValidationArray);
};

export interface Props {
  handleSubmit: SubmitHandler<EnterForm, EnterForm, string>;
}

const LoginRegistration_ = ({handleSubmit}: Props) => {
  const dispatch = useAppDispatch();

  const [submitting, setSubmitting] = useState<boolean>(false);

  const loginResponse = useSelector(loginResponseSelector());
  const login = useSelector(loginSelector());

  const needCard = !!(loginResponse && loginResponse.loginType !== LoginType.EskNumber);

  useEffect(() => {
    if (loginResponse && !loginResponse.isMobilePhoneSet) {
      dispatch(setRuntimeLoginStepState(LoginState.phoneNumberCheck));
      dispatch(initialize(Forms.phoneNumberCheckForm, {identificator: login, agreement: false}));
      return;
    }
    if (loginResponse && loginResponse.isMobilePhoneSet && loginResponse.isPasswordSet) {
      dispatch(setRuntimeLoginStepState(LoginState.login));
      dispatch(initialize(Forms.loginForm, {identificator: login}));
      return;
    }
    if (loginResponse && loginResponse.isMobilePhoneSet && loginResponse.loginType === LoginType.EskNumber) {
      dispatch(setRuntimeLoginStepState(LoginState.agreement));
      dispatch(initialize(Forms.registrationOrRecoveryForm, {identificator: login, agreement: false}));
      return;
    }
  }, [loginResponse, login, dispatch]);

  const onMainClick = async (formData: EnterForm) => {
    // trick for mobile production build
    if (formData?.identificator === '0000000001') Settings.setSettings(ApiEnvironments.test);
    if (formData?.identificator === '0000000002') Settings.setSettings(ApiEnvironments.stage);

    const submissionError = getFormErrors(formData, needCard);
    if (submissionError) {
      throw new SubmissionError(submissionError);
    }

    try {
      setSubmitting(true);
      await dispatch(checkLogin(onlyDigitsPipe(formData.cardNumber || formData.identificator || '')));
    } catch (e) {
      const error = e as ValidationError;
      const errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
      const formattedError = new FormValidationError(errorText, true);

      if (!formData.cardNumber) {
        throw new SubmissionError({
          identificator: formattedError,
        } as FormErrors<any, FormValidationError>);
      }

      throw new SubmissionError({
        cardNumber: formattedError,
      } as FormErrors<any, FormValidationError>);
    } finally {
      setSubmitting(false);
    }
  };
  const onEsiaClick = async () => {
    setSubmitting(true);
    try {
      await dispatch(await getEsiaLink());
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoFill = (fieldName: string) => (data: any) => {
    dispatch(change(Forms.enterForm, fieldName, data));
    requestAnimationFrame(handleSubmit(onMainClick)); // we here replaced setTimeout with requestAnimationFrame https://stackoverflow.com/a/4574953
    // setTimeout(handleSubmit(onMainClick), 0);
    // why do we use setTimeout with 0 https://stackoverflow.com/a/67657258
  };

  return (
    <View
      onAutoFill={handleAutoFill}
      onSubmit={(e) => {
        e?.preventDefault();
        handleSubmit(onMainClick)();
      }}
      onEsiaClick={(e) => {
        e?.preventDefault();
        onEsiaClick();
      }}
      needCard={needCard}
      loginType={loginResponse?.loginType}
      submitting={submitting}
    />
  );
};

export const Enter = reduxForm<any, any>({form: Forms.enterForm})(LoginRegistration_);
